import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GameHero = ({ game, onPlayGame, onToggleFavorite, isFavorite, userStats }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayClick = () => {
    setIsPlaying(true);
    onPlayGame();
  };

  // Create media array from game data
  const mediaArray = [
    // Cover image first
    {
      type: "image",
      url: game?.cover_image_url,
      caption: `${game?.title} - Cover`
    },
    // Trailer if available
    ...(game?.trailer_url ? [{
      type: "video",
      url: game?.trailer_url,
      caption: "Gameplay Trailer"
    }] : [])
  ].filter(media => media.url);

  const nextMedia = () => {
    setCurrentMediaIndex((prev) => 
      prev === mediaArray.length - 1 ? 0 : prev + 1
    );
  };

  const prevMedia = () => {
    setCurrentMediaIndex((prev) => 
      prev === 0 ? mediaArray.length - 1 : prev - 1
    );
  };

  const getCategoryNames = () => {
    return game?.game_category_mappings?.map(mapping => mapping.game_categories.name) || [];
  };

  const getStudioName = () => {
    return game?.gaming_studios?.name || 'Unknown Studio';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPlaytime = () => {
    if (!game?.estimated_play_time) return 'Unknown avg playtime';
    const totalMinutes = parseInt(game.estimated_play_time);
    if (isNaN(totalMinutes) || totalMinutes <= 0) return 'Unknown avg playtime';
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m avg playtime` : `${hours}h avg playtime`;
    } else {
      return `${minutes}m avg playtime`;
    }
  };

  const getAverageRating = () => {
    // Try multiple possible rating fields
    const rating = game?.average_rating || game?.rating || 0;
    return rating > 0 ? rating.toFixed(1) : '0.0';
  };

  const getPlayCount = () => {
    return game?.play_count || game?.reviewCount || 0;
  };

  return (
    <div className="relative bg-gradient-to-b from-background to-card">
      {/* Hero Media Section */}
      <div className="relative aspect-video lg:aspect-[21/9] overflow-hidden">
        <Image
          src={mediaArray[currentMediaIndex]?.url}
          alt={mediaArray[currentMediaIndex]?.caption || `${game?.title} - Media`}
          className="w-full h-full object-cover"
        />
        
        {/* Media Navigation */}
        {mediaArray.length > 1 && (
          <>
            <button
              onClick={prevMedia}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-background/90 transition-colors"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
            <button
              onClick={nextMedia}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-background/90 transition-colors"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </>
        )}

        {/* Media Indicators */}
        {mediaArray.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
            {mediaArray.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentMediaIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentMediaIndex ? 'bg-primary' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
      </div>

      {/* Game Info Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between space-y-4 lg:space-y-0">
            {/* Game Title & Info */}
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                {getCategoryNames().map((category, index) => (
                  <span key={index} className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium rounded">
                    {category}
                  </span>
                ))}
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={16} className="text-yellow-500 fill-current" />
                  <span className="text-sm font-medium">{getAverageRating()}</span>
                  <span className="text-sm text-muted-foreground">({getPlayCount()} plays)</span>
                </div>
              </div>
              
              <h1 className="text-3xl lg:text-5xl font-bold text-foreground mb-2">
                {game?.title}
              </h1>
              
              <p className="text-muted-foreground text-sm lg:text-base mb-4 max-w-2xl">
                {game?.description ? 
                  (game.description.length > 200 ? 
                    `${game.description.substring(0, 200)}...` : 
                    game.description
                  ) : 
                  'No description available'
                }
              </p>
              
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>By {getStudioName()}</span>
                <span>•</span>
                <span>{formatDate(game?.published_at)}</span>
                <span>•</span>
                <span>{formatPlaytime()}</span>
              </div>

              {/* User Stats */}
              {userStats && (
                <div className="mt-4 flex items-center space-x-4 text-sm text-muted-foreground">
                  <span>Your playtime: {userStats.playtime}</span>
                  <span>•</span>
                  <span>Last played: {userStats.lastPlayed}</span>
                  {userStats.progress > 0 && (
                    <>
                      <span>•</span>
                      <span>Progress: {userStats.progress}%</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="lg"
                iconName="Heart"
                onClick={onToggleFavorite}
                className={isFavorite ? "text-red-500" : ""}
              >
                {isFavorite ? "Remove from Favorites" : "Add to Favorites"}
              </Button>
              
              <Button
                variant="default"
                size="lg"
                iconName="Play"
                iconPosition="left"
                onClick={handlePlayClick}
                loading={isPlaying}
                className="bg-primary hover:bg-primary/90"
              >
                {isPlaying ? "Launching..." : "Play Now"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameHero;
